import { distanceMi, type LatLon } from './geo';

/**
 * Where a flight is and when it will land, worked out on the device from the last times we
 * heard. Needs no connection, so it keeps running in the air: the app refreshes the flight
 * right up to pushback, so the delay, the filed flying time and the expected taxi go with it,
 * and from takeoff the clock moves the plane along. With wifi on board, the plane's reported
 * position and ground speed take over and the landing time tightens up.
 */

const MIN = 60_000;
/** Typical taxi times when the airline hasn't said. */
const TAXI_OUT = 16 * MIN;
const TAXI_IN = 8 * MIN;

export type FlightPhase =
  | 'scheduled'
  | 'boarding'
  | 'taxiing'
  | 'air'
  | 'landed'
  | 'arrived'
  | 'cancelled';

export interface LiveTimes {
  cancelled?: boolean;
  scheduledOut?: number | null;
  scheduledOff?: number | null;
  scheduledOn?: number | null;
  scheduledIn?: number | null;
  estimatedOut?: number | null;
  estimatedOff?: number | null;
  estimatedOn?: number | null;
  estimatedIn?: number | null;
  actualOut?: number | null;
  actualOff?: number | null;
  actualOn?: number | null;
  actualIn?: number | null;
  lastPolledAt?: number | null;
  /** Filed time in the air (ms). */
  filedAirTime?: number | null;
  position?: {
    lat: number;
    lon: number;
    groundspeedKt: number | null;
    at: number;
  } | null;
}

/** Where the flight goes, for estimates from the plane's position. */
export interface FlightEnds {
  from: LatLon | null;
  to: LatLon | null;
}

/** The phone's connection: whether it's online, and since when it hasn't been. */
export interface PhoneLink {
  online: boolean;
  /** Last moment the phone was known to be online, while it's offline now. */
  offlineSince: number | null;
}

/** Live data newer than this, with the phone online, is trusted to say "not airborne yet". */
const HOLD_FRESH = 10 * MIN;

export type EstimateSource = 'position' | 'live' | 'last-known' | 'schedule';

export interface FlightEstimate {
  phase: FlightPhase;
  /** Gate departure and arrival, best guess. */
  out: number;
  in: number;
  /** Takeoff and landing, best guess. */
  off: number;
  on: number;
  /** Minutes late against the schedule (negative is early), at departure and arrival. */
  departDelay: number;
  arriveDelay: number;
  /** Share of the air time flown, 0 to 1. */
  progress: number;
  /** Until landing (in the air) or gate arrival (taxiing in). */
  minutesLeft: number | null;
  /** True when no live data has come in, or the last of it is over 30 minutes old. */
  offline: boolean;
  /** When live data last arrived. */
  updatedAt: number | null;
  /** What the landing time rests on, best first. */
  source: EstimateSource;
  /** Miles to go, when the route is known. */
  milesLeft: number | null;
}

/** A position is good for this long; after that dead reckoning from it is no better than the clock. */
const POSITION_FRESH = 15 * MIN;
/** Descent and approach take longer than cruising the same distance. */
const APPROACH = 8 * MIN;

const first = (...v: (number | null | undefined)[]) => v.find((x) => x != null) ?? null;

export function estimateFlight(
  plan: { startAt: number; endAt: number | null; cancelled?: boolean },
  live: LiveTimes | null | undefined,
  now: number,
  ends?: FlightEnds,
  link?: PhoneLink,
): FlightEstimate {
  const l = live ?? {};
  const schedOut = first(l.scheduledOut, plan.startAt) as number;
  const schedIn = first(l.scheduledIn, plan.endAt, schedOut + 2 * 60 * MIN) as number;
  // Air time from the schedule, which the delay doesn't change.
  const schedOff = first(l.scheduledOff, schedOut + TAXI_OUT) as number;
  const schedOn = first(l.scheduledOn, schedIn - TAXI_IN) as number;
  // The filed plan knows today's winds and routing; the schedule is padded.
  const planned =
    l.filedAirTime ??
    (l.estimatedOn != null && l.estimatedOff != null ? l.estimatedOn - l.estimatedOff : null);
  const airTime = Math.max(20 * MIN, planned ?? schedOn - schedOff);
  // Expected taxi out at this airport today, when the tracker gave one.
  const taxiOut =
    l.estimatedOff != null && l.estimatedOut != null
      ? Math.min(60 * MIN, Math.max(3 * MIN, l.estimatedOff - l.estimatedOut))
      : TAXI_OUT;

  let out = first(l.actualOut, l.estimatedOut, schedOut) as number;
  // The tracker has this flight (a poll that found nothing says nothing about takeoff).
  const tracked = l.scheduledOut != null || l.estimatedOut != null || l.actualOut != null;
  // Still at the gate when we last heard: it pushes back no sooner than that.
  if (tracked && l.actualOut == null && l.actualOff == null && l.lastPolledAt != null)
    out = Math.max(out, l.lastPolledAt);
  // Online with fresh word that it hasn't gone: it hasn't, however late that makes it.
  const holding =
    !!link?.online &&
    tracked &&
    l.actualOff == null &&
    l.lastPolledAt != null &&
    now - l.lastPolledAt < HOLD_FRESH;
  if (holding && l.actualOut == null) out = Math.max(out, now + MIN);
  // Offline: phones go quiet around pushback, so that's the soonest it could have gone.
  const quietSince = link && !link.online && l.actualOff == null ? link.offlineSince : null;
  if (quietSince != null && l.actualOut == null) out = Math.max(out, quietSince);
  // Pushed back but not airborne when we last heard: takeoff is no earlier than that.
  let off = first(
    l.actualOff,
    l.actualOut != null ? l.actualOut + taxiOut : null,
    l.estimatedOff,
    out + taxiOut,
  ) as number;
  if (l.actualOff == null) off = Math.max(off, out + (l.actualOut != null ? 0 : taxiOut));
  if (l.actualOff == null && l.actualOut != null && l.lastPolledAt != null)
    off = Math.max(off, l.lastPolledAt);
  if (holding) off = Math.max(off, now + MIN);
  if (quietSince != null && l.actualOut != null) off = Math.max(off, quietSince);
  // Once airborne, landing is takeoff plus air time unless the tracker gave a better time.
  let on = first(
    l.actualOn,
    l.actualOff != null ? first(l.estimatedOn, off + airTime) : null,
    l.estimatedOn,
    off + airTime,
  ) as number;
  if (l.actualOn == null && l.estimatedOn != null && l.actualOff != null) {
    // A stale estimate shouldn't land us in the past while we're still flying.
    on = Math.max(on, Math.min(now + MIN, off + airTime * 1.5));
  }
  // With a fresh position (wifi on board): distance left over ground speed, plus the approach.
  const pos = l.position;
  const to = ends?.to ?? null;
  const from = ends?.from ?? null;
  const fresh = pos != null && now - pos.at < POSITION_FRESH && l.actualOn == null;
  let source: EstimateSource = l.actualOff != null || l.actualOut != null ? 'live' : 'schedule';
  if (l.lastPolledAt != null && now - l.lastPolledAt > 30 * MIN && source === 'live')
    source = 'last-known';
  if (l.lastPolledAt != null && source === 'schedule') source = 'last-known';
  if (fresh && pos && to && pos.groundspeedKt != null && pos.groundspeedKt > 100) {
    const mph = pos.groundspeedKt * 1.15078;
    const left = distanceMi(pos, to);
    on = pos.at + (left / mph) * 3_600_000 + APPROACH;
    source = 'position';
  }
  const inn = first(
    l.actualIn,
    l.estimatedIn != null && l.estimatedIn >= on ? l.estimatedIn : null,
    on + TAXI_IN,
  ) as number;

  let phase: FlightPhase;
  if (plan.cancelled || l.cancelled) phase = 'cancelled';
  else if (l.actualIn != null || now >= inn) phase = 'arrived';
  else if (l.actualOn != null || now >= on) phase = 'landed';
  else if (l.actualOff != null || now >= off) phase = 'air';
  else if (l.actualOut != null || now >= out) phase = 'taxiing';
  else if (now >= out - 45 * MIN) phase = 'boarding';
  else phase = 'scheduled';

  const timeShare = Math.min(1, Math.max(0, (now - off) / Math.max(MIN, on - off)));
  const total = from && to ? distanceMi(from, to) : null;
  // By distance when we know where the plane is, else by time.
  const progress =
    phase === 'air'
      ? source === 'position' && pos && to && total
        ? Math.min(1, Math.max(0, 1 - distanceMi(pos, to) / total))
        : timeShare
      : phase === 'landed' || phase === 'arrived'
        ? 1
        : 0;
  const milesLeft = phase === 'air' && total != null ? Math.round(total * (1 - progress)) : null;
  const minutesLeft =
    phase === 'air'
      ? Math.max(0, Math.round((on - now) / MIN))
      : phase === 'landed'
        ? Math.max(0, Math.round((inn - now) / MIN))
        : null;

  return {
    phase,
    out,
    in: inn,
    off,
    on,
    departDelay: Math.round((out - schedOut) / MIN),
    arriveDelay: Math.round((inn - schedIn) / MIN),
    progress,
    minutesLeft,
    offline: l.lastPolledAt == null || now - l.lastPolledAt > 30 * MIN,
    updatedAt: l.lastPolledAt ?? null,
    source,
    milesLeft,
  };
}

/** "On time", "25 min late", "10 min early", "1 h 5 min late". */
export function delayLabel(minutes: number): string {
  if (Math.abs(minutes) < 5) return 'On time';
  const m = Math.abs(minutes);
  const text = m >= 60 ? `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`;
  return `${text} ${minutes > 0 ? 'late' : 'early'}`;
}
